/**
 * @changelog
 * | 版本   | 变更说明                                                     | 关联                |
 * | v1.0.0 | 新增 debug 模式：?debug=true 时启用 vConsole 与日志 zip 下载 | REQ: h5-debug-mode |
 * /@changelog
 *
 * @author fancylin
 */

import VConsole from 'vconsole';
import JSZip from 'jszip';

/** 单条日志记录 */
interface LogEntry {
  /** 时间戳（ISO 字符串） */
  time: string;
  /** 日志级别 */
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  /** 日志内容（已序列化为字符串） */
  message: string;
}

/** 内存中保留的最大日志条数，避免无限增长 */
const MAX_LOG_ENTRIES = 5000;

/** 各级别日志缓冲区 */
const logBuffers: Record<LogEntry['level'], LogEntry[]> = {
  log: [],
  info: [],
  warn: [],
  error: [],
  debug: [],
};

/** 全量日志缓冲（按时间排序的合集） */
const allLogs: LogEntry[] = [];

let installed = false;

/**
 * 将任意参数序列化为可读字符串
 * - 对象使用 JSON.stringify（带循环引用保护）
 * - Error 对象保留 stack
 */
function safeStringify(args: unknown[]): string {
  const seen = new WeakSet<object>();
  return args
    .map((arg) => {
      if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`;
      }
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.stringify(arg, (_key, value) => {
            if (typeof value === 'object' && value !== null) {
              if (seen.has(value as object)) return '[Circular]';
              seen.add(value as object);
            }
            return value;
          });
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    })
    .join(' ');
}

/** 推入一条日志，超出上限时按 FIFO 丢弃旧日志 */
function pushLog(level: LogEntry['level'], args: unknown[]): void {
  const entry: LogEntry = {
    time: new Date().toISOString(),
    level,
    message: safeStringify(args),
  };
  const buf = logBuffers[level];
  buf.push(entry);
  if (buf.length > MAX_LOG_ENTRIES) buf.shift();
  allLogs.push(entry);
  if (allLogs.length > MAX_LOG_ENTRIES * 2) allLogs.shift();
}

/**
 * 拦截 console.* 方法，在保留原行为的前提下把日志写入缓冲区。
 * 同时捕获全局 error / unhandledrejection 作为 error 日志。
 */
function installConsoleHook(): void {
  const levels: LogEntry['level'][] = ['log', 'info', 'warn', 'error', 'debug'];
  levels.forEach((level) => {
    const original = console[level].bind(console) as (...a: unknown[]) => void;
    console[level] = (...args: unknown[]) => {
      try {
        pushLog(level, args);
      } catch {
        // 拦截器自身异常不应影响业务 console
      }
      original(...args);
    };
  });

  window.addEventListener('error', (e) => {
    pushLog('error', [`[window.error] ${e.message}`, `at ${e.filename}:${e.lineno}:${e.colno}`, e.error]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    pushLog('error', ['[unhandledrejection]', e.reason]);
  });
}

/** 把单一级别日志格式化为可读文本 */
function formatLogText(entries: LogEntry[]): string {
  return entries.map((e) => `[${e.time}] [${e.level.toUpperCase()}] ${e.message}`).join('\n');
}

/** 收集环境信息（UA、URL、屏幕、时间等），便于排查 */
function collectEnvInfo(): string {
  const info: Record<string, unknown> = {
    时间: new Date().toISOString(),
    URL: window.location.href,
    UA: navigator.userAgent,
    语言: navigator.language,
    窗口尺寸: { width: window.innerWidth, height: window.innerHeight },
    屏幕尺寸: { width: window.screen?.width, height: window.screen?.height },
    像素比: window.devicePixelRatio,
    在线: navigator.onLine,
    日志条数: {
      log: logBuffers.log.length,
      info: logBuffers.info.length,
      warn: logBuffers.warn.length,
      error: logBuffers.error.length,
      debug: logBuffers.debug.length,
    },
  };
  return JSON.stringify(info, null, 2);
}

/** 触发浏览器下载 Blob */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // 延迟释放，避免 Safari 上断开下载流
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 把当前所有日志打包为 zip 并触发下载 */
async function downloadLogsZip(): Promise<void> {
  const zip = new JSZip();
  zip.file('env.txt', collectEnvInfo());
  zip.file('all.log', formatLogText(allLogs));
  (Object.keys(logBuffers) as LogEntry['level'][]).forEach((level) => {
    zip.file(`${level}.log`, formatLogText(logBuffers[level]));
  });
  const blob = await zip.generateAsync({ type: 'blob' });
  // 使用本地实际日期和时间，格式：YYYY-MM-DD_HH-mm-ss（避免 toISOString 输出 UTC 时间引起困惑）
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  triggerDownload(blob, `h5-logs-${date}_${time}.zip`);
}

/** 下载图标 SVG（线性、白色） */
const ICON_DOWNLOAD = `
<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M12 3v12"/>
  <path d="M7 10l5 5 5-5"/>
  <path d="M5 21h14"/>
</svg>`;

/** 加载中 spinner SVG（旋转） */
const ICON_SPINNER = `
<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
     stroke-width="2" stroke-linecap="round" aria-hidden="true"
     style="animation: debug-dl-spin 0.8s linear infinite;">
  <path d="M12 3a9 9 0 1 0 9 9" opacity="0.9"/>
</svg>`;

/**
 * 按钮组样式：
 * - 左侧自定义"下载日志"按钮（深绿）
 * - 右侧 vConsole 默认 switch 按钮（覆盖样式与下载按钮统一）
 * 两者拼成一个胶囊型按钮组：[ ⬇ | vConsole ]
 */
const BTN_STYLES = `
@keyframes debug-dl-spin { to { transform: rotate(360deg); } }

/* ===== 左：下载日志按钮 ===== */
#debug-download-logs-btn {
  position: fixed;
  /* 默认位置（vConsole 未就绪时的占位），后续 syncPosition 会接管 */
  right: 76px;
  bottom: 16px;
  z-index: 10001;
  /* 宽度收紧到与 vConsole switch 字宽相近，避免左侧块过宽显得不协调 */
  width: 40px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  /* 仅左侧圆角；右边与 vConsole switch 拼接 */
  border-radius: 15px 0 0 15px;
  background: #1b5e20;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
/* 点击/悬浮均不变色，保持与 vConsole switch 视觉一致 */
#debug-download-logs-btn:hover,
#debug-download-logs-btn:active,
#debug-download-logs-btn:focus { background: #1b5e20; outline: none; }
#debug-download-logs-btn:disabled { opacity: 0.7; cursor: progress; }
#debug-download-logs-btn svg { display: block; }

/* ===== 右：覆盖 vConsole 默认 switch 样式，与下载按钮拼成一体 ===== */
/* 使用较高优先级（双 id + !important）以压过 vConsole 自身样式 */
#__vconsole .vc-switch,
.vc-switch {
  /* 仅右侧圆角，左边与下载按钮拼接 */
  border-radius: 0 15px 15px 0 !important;
  /* 与下载按钮统一高度 / 字体 / 内边距 */
  height: 30px !important;
  min-width: 64px !important;
  padding: 0 12px !important;
  line-height: 30px !important;
  font-size: 12px !important;
  /* 与下载按钮同色系（稍浅的深绿，区分但贴合） */
  background-color: #2e7d32 !important;
  color: #fff !important;
  /* 去除默认阴影，避免与下载按钮之间出现暗缝 */
  box-shadow: none !important;
  border: none !important;
}
#__vconsole .vc-switch:hover,
.vc-switch:hover {
  background-color: #388e3c !important;
}
`;

/** 注入一次按钮样式表 */
function injectStylesOnce(): void {
  if (document.getElementById('debug-download-logs-style')) return;
  const styleEl = document.createElement('style');
  styleEl.id = 'debug-download-logs-style';
  styleEl.textContent = BTN_STYLES;
  document.head.appendChild(styleEl);
}

/**
 * 让"下载日志"按钮的位置紧贴 vConsole switch 按钮的左侧（间距 8px）。
 * vConsole 用户拖动时会更新自身按钮的 inline style，这里通过
 * MutationObserver 监听其属性变化实时跟随。
 */
function syncPositionToVConsole(btn: HTMLButtonElement): void {
  const update = () => {
    const sw = document.querySelector('.vc-switch') as HTMLElement | null;
    if (!sw) return;
    const rect = sw.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // 计算 right / bottom（基于视口右下角，便于在屏幕旋转/缩放时保持相对位置）
    const right = window.innerWidth - rect.right;
    const bottom = window.innerHeight - rect.bottom;
    /* eslint-disable no-param-reassign */
    // 紧贴 vConsole switch 左侧（间距 0），形成按钮组
    btn.style.right = `${right + rect.width}px`;
    btn.style.bottom = `${bottom}px`;
    // 高度对齐 switch 按钮
    btn.style.height = `${rect.height}px`;
    /* eslint-enable no-param-reassign */
  };

  // vConsole switch 按钮可能在 setupDebug 之后才插入，这里轮询一段时间
  let tries = 0;
  const timer = window.setInterval(() => {
    const sw = document.querySelector('.vc-switch');
    tries += 1;
    if (sw) {
      window.clearInterval(timer);
      update();
      // 监听 switch 按钮的 style 变化（拖动会改变 right/bottom）
      const mo = new MutationObserver(update);
      mo.observe(sw, { attributes: true, attributeFilter: ['style', 'class'] });
      window.addEventListener('resize', update);
      window.addEventListener('orientationchange', update);
    } else if (tries > 50) {
      // 5s 内仍未找到则放弃跟随，保持默认位置
      window.clearInterval(timer);
    }
  }, 100);
}

/** 创建与 vConsole 视觉融合的"下载日志"图标按钮 */
function createDownloadButton(): void {
  if (document.getElementById('debug-download-logs-btn')) return;
  injectStylesOnce();

  const btn = document.createElement('button');
  btn.id = 'debug-download-logs-btn';
  btn.type = 'button';
  btn.title = '下载日志 zip';
  btn.setAttribute('aria-label', '下载日志');
  btn.innerHTML = ICON_DOWNLOAD;

  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.innerHTML = ICON_SPINNER;
    downloadLogsZip()
      .catch((err) => {
        console.error('[debug] 下载日志失败', err);
        alert('下载日志失败，请查看 console');
      })
      .finally(() => {
        btn.disabled = false;
        btn.innerHTML = ICON_DOWNLOAD;
      });
  });

  const mount = () => {
    document.body.appendChild(btn);
    syncPositionToVConsole(btn);
  };

  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', mount);
  }
}

/**
 * 判断当前 URL 是否启用 debug 模式
 *
 * 实现策略：直接对 `location.href` 做正则扫描，不区分 search / hash。
 * 这样可以同时兼容：
 * 1. 普通 query：           ?debug=true
 * 2. hash 模式 query：       #/path?debug=true
 * 3. hash 路径外（很少见）：  ?a=b#/p?debug=true / #?debug=true
 * 4. 多 query 拼接：         ?sceneUrl=https://x.com/a.json&debug=true
 *    （不会被 sceneUrl 中的 https:// 干扰，因为我们用 [?&] 作为前置分隔符）
 *
 * 命中以下任一形式即视为启用：
 *   ?debug   ?debug=   ?debug=true   ?debug=1
 *   &debug   &debug=   &debug=true   &debug=1
 */
export function isDebugMode(): boolean {
  try {
    const href = window.location.href || '';
    // [?&] 前缀确保 'debug' 是独立参数而非其他参数值的一部分
    // 后跟：边界（&/#/$）、=空、=true、=1
    const re = /[?&]debug(?:=(?:true|1)?)?(?=&|#|$)/i;
    return re.test(href);
  } catch {
    return false;
  }
}

/**
 * 启用 debug 模式：
 * 1. 安装 console 拦截器（必须最早执行，确保启动期日志也能被捕获）
 * 2. 实例化 vConsole
 * 3. 在页面右下角注入"下载日志"按钮
 *
 * 幂等：重复调用不会重复挂载。
 */
export function setupDebug(): void {
  if (installed) return;
  installed = true;

  installConsoleHook();
  // eslint-disable-next-line no-new
  new VConsole();
  createDownloadButton();

  console.info('[debug] debug 模式已启用，可点击右下角按钮下载日志 zip');
}
